import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, Grid, Card, CardContent, 
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, CircularProgress, Alert, IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';

import superAdminService from '../services/superAdminService';

const SuperAdminDealerDetails = () => {
  const { dealerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Stores the ID of the user currently being updated
  const [actionLoading, setActionLoading] = useState(null); 

  const fetchDetails = useCallback(async () => {
    try {
      const result = await superAdminService.getDealerDetails(dealerId);
      setData(result);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleRoleChange = async (userId, action) => {
    if(!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    setActionLoading(userId); // Start Spinner
    try {
        if(action === 'promote') await superAdminService.promoteUser(userId);
        else await superAdminService.demoteUser(userId);
        
        await fetchDetails(); // Refresh Data
    } catch (err) {
        alert("Action failed: " + err.message);
    } finally {
        setActionLoading(null); // Stop Spinner
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove user "${userName}"?\n\nThey will lose access immediately.`)) {
        return;
    }

    setActionLoading(userId);
    try {
        await superAdminService.deleteUser(userId);
        await fetchDetails(); // Refresh list
    } catch (err) {
        alert(err.message);
    } finally {
        setActionLoading(null);
    }
  };


  if (loading) return <CircularProgress sx={{ m: 5 }} />;
  if (!data) return <Alert severity="error">Dealer not found.</Alert>;

  const dealer = data.dealer || data.Dealer || {};
  const stats = data.stats || data.Stats || {};
  const users = data.users || data.Users || [];
  const quotes = data.quotes || data.Quotes || [];
  const surveys = data.surveys || data.Surveys || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/super-admin/dealers')} sx={{ mb: 2 }}>
        Back to List
      </Button>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
            {dealer.name || dealer.Name || 'Unknown Dealer'}
        </Typography>
        <Typography color="text.secondary">
            {dealer.brandName || dealer.BrandName} • {dealer.contactEmail || dealer.ContactEmail}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#eff6ff' }}>
                <CardContent>
                    <Typography variant="h6">{stats.totalUsers || stats.TotalUsers || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Users</Typography>
                </CardContent>
            </Card>
        </Grid>
        <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#f0fdf4' }}>
                <CardContent>
                    <Typography variant="h6">{stats.totalQuotes || stats.TotalQuotes || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Quotes</Typography>
                </CardContent>
            </Card>
        </Grid>
        <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#fdf2f8' }}>
                <CardContent>
                    <Typography variant="h6">{stats.totalSurveys || stats.TotalSurveys || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Surveys</Typography>
                </CardContent>
            </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} indicatorColor="primary" textColor="primary">
          <Tab label="User Roster" />
          <Tab label="Activity Log" />
        </Tabs>
      </Paper>

      {/* USER ROSTER TAB */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const uId = user.id || user.Id;
                const uName = user.name || user.Name;
                const uEmail = user.email || user.Email;
                const uRole = user.role || user.Role;
                const isLoading = actionLoading === uId;

                return (
                    <TableRow key={uId}>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon color="action" />
                            {uName}
                        </Box>
                    </TableCell>
                    <TableCell>{uEmail}</TableCell>
                    <TableCell>
                        <Chip 
                            label={uRole} 
                            color={uRole === 'ADMIN' ? 'primary' : 'default'} 
                            size="small" 
                            icon={uRole === 'ADMIN' ? <SupervisorAccountIcon /> : undefined}
                        />
                    </TableCell>
                    <TableCell align="right">
                        {isLoading ? (
                            <CircularProgress size={24} sx={{ color: '#a8851d' }} />
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                {uRole === 'USER' ? (
                                    <Button 
                                        size="small" 
                                        color="success"
                                        variant="outlined"
                                        startIcon={<ArrowUpwardIcon />}
                                        onClick={() => handleRoleChange(uId, 'promote')}
                                    >
                                        Promote
                                    </Button>
                                ) : (
                                    <Button 
                                        size="small" 
                                        color="warning"
                                        variant="outlined"
                                        startIcon={<ArrowDownwardIcon />}
                                        onClick={() => handleRoleChange(uId, 'demote')}
                                    >
                                        Demote
                                    </Button>
                                )}

                                <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeleteUser(uId, uName)}
                                    title="Delete User"
                                    sx={{
                                        '&:hover': {
                                            bgcolor: 'rgba(211, 47, 47, 0.08)'
                                        }
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </TableCell>
                    </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ACTIVITY LOG TAB */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 2 }}>Recent Quotes</Typography>
                {quotes.length === 0 ? <Typography color="text.secondary">No quotes found.</Typography> : (
                    <Paper>
                        <Table size="small">
                            <TableBody>
                                {quotes.map(q => (
                                    <TableRow key={q.id || q.Id}>
                                        <TableCell>{q.name || q.Name}</TableCell>
                                        <TableCell>{new Date(q.createdAt || q.CreatedAt).toLocaleDateString()}</TableCell>
                                        <TableCell><Chip label={q.status || q.Status} size="small" /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Grid>
            
            <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 2 }}>Recent Surveys</Typography>
                {surveys.length === 0 ? <Typography color="text.secondary">No surveys found.</Typography> : (
                    <Paper>
                        <Table size="small">
                            <TableBody>
                                {surveys.map(s => (
                                    <TableRow key={s.id || s.Id}>
                                        <TableCell>{s.title || s.Title}</TableCell>
                                        <TableCell>{new Date(s.createdAt || s.CreatedAt).toLocaleDateString()}</TableCell>
                                        <TableCell>{s.responseCount || s.ResponseCount} responses</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SuperAdminDealerDetails;